import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da08qumth95mubjtue50";

interface Status {
  type: "success" | "error";
  msg: string;
}

export default function ContactForm() {
  const [formError, setFormError] = useState("");
  const [status, setStatus] = useState<Status | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("contact_alt") || "").trim();
    // 蜜罐字段被填充则静默当作成功提交，避免直接反馈给机器人
    if (honeypot) {
      setFormError("");
      setStatus({ type: "success", msg: "感谢你的留言，我们会尽快与你联系！" });
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "contact_alt") payload.append(key, String(value));
    });

    setSubmitting(true);
    setFormError("");
    setStatus(null);
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(String(serverMsg));
      if (!res.ok || parsed?.code !== "OK" || isSpam) {
        const msg =
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "提交失败，请稍后再试";
        setFormError(msg);
        setStatus({ type: "error", msg });
        return;
      }
      setStatus({ type: "success", msg: "感谢你的留言，我们会尽快与你联系！" });
      form.reset();
    } catch {
      setFormError("网络异常，请稍后重试");
      setStatus({ type: "error", msg: "网络异常，请稍后重试" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-readdy-form="动漫网联系我们表单"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你的称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            maxLength={30}
            placeholder="如何称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱地址
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={60}
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
          留言主题
        </label>
        <input
          id="subject"
          name="subject"
          type="text"
          maxLength={80}
          placeholder="想咨询或反馈的内容"
          className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          required
          maxLength={500}
          placeholder="请写下你的想法、建议或合作意向（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
      </div>

      {/* 反垃圾蜜罐字段 */}
      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="hp-field"
      />

      <div className="mt-5">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {submitting ? "提交中..." : "提交留言"}
          <i className="ri-send-plane-line"></i>
        </button>
      </div>

      {formError && (
        <p role="alert" className="mt-4 rounded-md bg-primary-100/70 px-4 py-3 text-sm text-primary-900">
          {formError}
        </p>
      )}
      {status?.type === "success" && (
        <p role="status" className="mt-4 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          {status.msg}
        </p>
      )}
    </form>
  );
}